import type { ReactNode, Ref } from "react";

import { cn } from "./cn";
import { Icon } from "./icon";

/** The text and the kind of a result shown under a form or a section. A `conflict` is a refusal of a stale save, not an error of the staff member. */
export type FormMessage = Readonly<{ tone: "success" | "error" | "conflict"; text: ReactNode }>;

const tones: Record<FormMessage["tone"], string> = {
  success: "border-success-border bg-success-soft text-success",
  error: "border-destructive-border bg-destructive-soft text-destructive",
  conflict: "border-warning-border bg-warning-soft text-warning",
};

/** A message under a section. A success is announced politely; an error or a conflict at once. It has a glyph and words, never colour alone. */
export function StatusMessage({
  message,
  focusRef,
}: Readonly<{
  message: FormMessage | null;
  /** Makes the message focusable by code (`tabIndex=-1`) and gives the ref, so a dialog can move focus to a failure. */
  focusRef?: Ref<HTMLParagraphElement>;
}>) {
  if (message === null) return null;
  const urgent = message.tone !== "success";
  const glyph = message.tone === "success" ? "checkmark-circle-02" : "alert-02";
  return (
    <p
      className={cn(
        "m-0 flex items-start gap-2 rounded-sm border px-3.5 py-2.5 text-sm leading-6",
        tones[message.tone],
      )}
      role={urgent ? "alert" : "status"}
      ref={focusRef}
      tabIndex={focusRef ? -1 : undefined}
    >
      <Icon name={glyph} className="mt-1 size-4 shrink-0" />
      <span>{message.text}</span>
    </p>
  );
}
